import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, KeyRound, PenLine, Settings, Mail, Sun, Moon, ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Wabi 家族 shell（washi 主题）——「茶室 / 留白」。
 *
 * 侘寂的「安静」范式（与六家族正交）：
 * - 静谧横向顶栏：wordmark + 稀疏【句式小写文字导航】（激活 = 枯枝褐 1px 下划线，非图标 rail、非 luxury 大写、非 ink 竖排）
 * - 内容居中窄栏（max-w-5xl）+ 巨垂直留白（py-16/20）= ma(間)
 * - 页眉：枯枝褐短丝线 + kicker（对应 luxury 金线但全换褐）
 * - 慢柔淡入 + 轻位移（easeOutExpo 0.5s，无弹跳无横移无缩放）
 *
 * 枯枝褐 = washi 的 primary token。
 */
export function WabiShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((state) => state.user);
  const { navItems } = useNavigation();
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <WabiNav pathname={pathname} navItems={navItems} isActive={isActive} />

      {/* 二级句式小写子链（· 分隔）*/}
      {subNav.length > 0 && (
        <nav className="border-b border-[color:hsl(var(--surface-border))]">
          <div className="mx-auto max-w-5xl flex items-center gap-3 px-6 md:px-10 h-10 overflow-x-auto text-[13px] text-muted-foreground">
            {subNav.map((child, idx) => {
              const childActive = isActive(child.href);
              return (
                <span key={child.href} className="flex items-center gap-3 whitespace-nowrap">
                  {idx > 0 && <span className="text-muted-foreground/40">·</span>}
                  <Link
                    to={child.href}
                    className={cn(
                      'tracking-[0.02em] transition-colors',
                      childActive ? 'text-foreground' : 'hover:text-foreground',
                    )}
                  >
                    {t(child.labelKey)}
                  </Link>
                </span>
              );
            })}
          </div>
        </nav>
      )}

      <main id="main-content" className="flex-1 px-6 md:px-10 py-16 md:py-20">
        <div className="mx-auto max-w-5xl">
          <ProfileReviewAlert />

          {/* 页眉：枯枝褐短丝线 + kicker */}
          <div className="mb-10 flex items-center gap-3">
            <span className="h-px w-8 bg-primary" />
            <span className="text-[11px] tracking-[0.3em] uppercase font-medium text-primary">
              Dashboard
            </span>
          </div>

          <AnimatePresence>
            <motion.div
              key={pathname}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <ConfigurableFooter variant="dashboard" config={footerConfig} />

      {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 静谧横向顶栏                                                        */
/* ------------------------------------------------------------------ */

function WabiNav({
  pathname,
  navItems,
  isActive,
}: {
  pathname: string;
  navItems: ReturnType<typeof useNavigation>['navItems'];
  isActive: (href: string) => boolean;
}) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const t = useTranslations('nav');
  const tAuth = useTranslations('auth');
  const tUsers = useTranslations('users');
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');

  return (
    <header className="sticky top-0 z-40 border-b border-[color:hsl(var(--surface-border))] bg-background/95">
      <div className="mx-auto max-w-5xl flex h-16 items-center gap-4 px-6 md:px-10">
        <div className="md:hidden">
          <MobileSidebar />
        </div>

        <BrandLogo variant="auto" size="sm" linkTo="/" />

        {/* 句式小写文字导航 */}
        <nav className="hidden md:flex items-center gap-7 ml-4 flex-1 min-w-0 overflow-x-auto">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'whitespace-nowrap text-[14px] normal-case tracking-normal font-normal transition-colors',
                  'underline-offset-4 decoration-1',
                  active
                    ? 'text-primary underline'
                    : 'text-muted-foreground hover:text-foreground no-underline',
                )}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        <span className="flex-1 md:hidden" />

        <div className="flex items-center gap-1">
          {guidedEligible && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 tracking-normal"
              onClick={() => setUiMode('guided')}
              aria-label={tGuided('switchGuided')}
              title={tGuided('switchGuided')}
            >
              <Sparkles className="h-4 w-4" />
              <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
            </Button>
          )}
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={tCommon('toggleTheme')}
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9"
            onClick={() => navigate('/dashboard/messages')}
            aria-label={tCommon('openMessages')}
          >
            <Mail className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[6px] h-[6px] rounded-full bg-accent" />
            )}
          </Button>
          <WabiUserMenu tAuth={tAuth} tUsers={tUsers} />
        </div>
      </div>
    </header>
  );
}

function WabiUserMenu({
  tAuth,
  tUsers,
}: {
  tAuth: ReturnType<typeof useTranslations>;
  tUsers: ReturnType<typeof useTranslations>;
}) {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-full p-0.5 pr-2 hover:bg-overlay/60 transition-colors cursor-pointer">
          <Avatar className="h-8 w-8 rounded-full">
            <AvatarImage src="/avatar.png" />
            <AvatarFallback className="rounded-full text-[11px]">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden sm:block" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium truncate">{user?.displayName || user?.email}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
          <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
            <PenLine className="h-4 w-4" />
            {tUsers('changeName')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {tUsers('changePassword')}
          </DropdownMenuItem>
          {isPrivileged && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="h-4 w-4" />
                {tAuth('logout.settings')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
            <LogOut className="h-4 w-4" />
            {tAuth('logout.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </>
  );
}
